import type { StatusDef } from './statusRegistry'

/** Shape shared by `BUILTIN_STATUS_GROUPS` entries — kept generic so callers
 *  don't need to import the concrete type from statusRegistry just to pass
 *  its return value through. */
export interface StatusGroupEntry {
  group: string
  label: string
  statuses: StatusDef[]
}

/**
 * Filter status groups for the StatusMenu search box.
 *
 * Case-insensitive substring match against each status's `name` or `key`.
 * A blank/whitespace-only query is treated as "no filter" — every status
 * passes through untouched. Either way, groups left with zero statuses are
 * dropped entirely: this is the same `.filter((g) => g.statuses.length > 0)`
 * pattern StatusMenu already ran against the unfiltered registry (so an
 * empty built-in group never rendered a header with nothing under it) —
 * extended to run on the filtered set instead of being a second, parallel
 * filter path.
 */
export function filterStatusGroups<G extends StatusGroupEntry>(
  groups: G[],
  query: string
): G[] {
  const q = query.trim().toLowerCase()
  return groups
    .map((g) => ({
      ...g,
      statuses: q
        ? g.statuses.filter(
            (s) => s.name.toLowerCase().includes(q) || s.key.toLowerCase().includes(q)
          )
        : g.statuses,
    }))
    .filter((g) => g.statuses.length > 0)
}
